import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";
import { getDebouncedCall } from "../../../../../customHooks/useDebouncedSearch";
import {
  formatToCurrency,
  parseStringToNumber,
} from "../../../../../helperFunctions";
import {
  SingleCooperativeDetails,
  SingleMemberContributionDetails,
} from "../../../../../redux/features/cooperative/cooperative.type";
import {
  LoanProduct,
  RequestLoanPayload,
} from "../../../../../redux/features/loans/loans.type";
import { useLazyGetCooperativeMembersQuery } from "../../../../../redux/features/membership/membership.api";
import { useAppSelector } from "../../../../../redux/hooks";
import { getGuarantorInformationSchema } from "../../../../../validationSchema";
import { Button } from "../../../../buttons/button";
import { FormCard } from "../../../../cards/formCard";
import { InformationCard } from "../../../../cards/infoCard";
import { Icon } from "../../../../icons/icon";
import ComboBox from "../../../../inputs/combobox";
import { TextInput } from "../../../../inputs/textInput";

const debouncedMemberSearch = getDebouncedCall(1000);

export const GuarantorInformation = ({
  cooperativeDetails,
  onSuccess,
  finalPayload,
  setFinalPayload,
  selectedLoanProduct,
  memberContributionsDetails,
}: {
  cooperativeDetails: SingleCooperativeDetails;
  onSuccess: () => void;
  finalPayload?: Partial<RequestLoanPayload>;
  setFinalPayload: (data: Partial<RequestLoanPayload>) => void;
  selectedLoanProduct?: LoanProduct;
  memberContributionsDetails: SingleMemberContributionDetails;
}) => {
  const [totalGuarantorAmountLeft, setTotalGuarantorAmountLeft] =
    useState<number>(0);
  const [totalGuarantorAmountNeeded, setTotalGuarantorAmountNeeded] =
    useState<number>(0);

  const { profile } = useAppSelector((state) => state.auth);

  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
    handleBlur,
    handleChange,
  } = useFormik({
    initialValues: {
      name: "",
      email: "",
      amount: "",
    },
    enableReinitialize: true,
    validationSchema: getGuarantorInformationSchema(totalGuarantorAmountLeft),
    onSubmit: (data, { resetForm }) => {
      const currentGuarantors = finalPayload?.guarantor ?? [];

      setFinalPayload({
        ...finalPayload,
        guarantor: [
          ...currentGuarantors,
          {
            email: data.email,
            amount: parseStringToNumber(data.amount)!,
          },
        ],
      });

      resetForm();
    },
  });

  const [fetchMembers, { data: members, isFetching: fetchingMembers }] =
    useLazyGetCooperativeMembersQuery();

  useEffect(() => {
    debouncedMemberSearch(() => {
      fetchMembers({
        coop: cooperativeDetails.id,
        text: values?.name,
        offset: 0,
        limit: 10,
      });
    });
    // eslint-disable-next-line
  }, [values.name]);

  useEffect(() => {
    const gurantorAmount =
      finalPayload?.guarantor?.reduce(
        (sum, item) => sum + (item.amount ?? 0),
        0
      ) ?? 0;

    const amountNeeded =
      (finalPayload?.loanAmount ?? 0) -
      (parseStringToNumber(memberContributionsDetails?.Balance) ?? 0);

    setTotalGuarantorAmountNeeded(amountNeeded);

    setTotalGuarantorAmountLeft(amountNeeded - gurantorAmount);
  }, [finalPayload, memberContributionsDetails]);

  return (
    <form className="flex flex-col gap-4 min-h-full" onSubmit={handleSubmit}>
      <p className="text-base font-bold">
        Add Guarantors ({finalPayload?.guarantor?.length ?? 0}/
        {selectedLoanProduct?.MaxGuarantor})
      </p>

      {totalGuarantorAmountLeft === 0 ? undefined : (
        <>
          <InformationCard
            info={
              <span>
                You’ll need a guarantor or guarantors for a total amount of{" "}
                <b>
                  {formatToCurrency(totalGuarantorAmountNeeded, {
                    currency: "NGN",
                    decimalPlaces: 2,
                  })}
                </b>{" "}
                to proceed.
              </span>
            }
            variant="purple"
            size="mini"
          />

          <ComboBox
            type="text"
            isDropdown
            name="name"
            value={values.name}
            menuItems={
              members?.data?.results
                ?.filter((m) => {
                  return (
                    m.User.Email !== profile?.Email &&
                    ![
                      ...(finalPayload?.guarantor?.map((g) => g.email) ?? []),
                    ].includes(m.User.Email)
                  );
                })
                ?.map((member) => ({
                  name: `${member.User.FirstName} ${member.User.LastName}`,
                  value: {
                    name: `${member.User.FirstName} ${member.User.LastName}`,
                    value: member.User.Email,
                  },
                })) ?? []
            }
            placeholder="Type to search..."
            onChange={(e) => {
              handleChange(e);
              setFieldValue("email", "");
            }}
            onSearch={(search) => {
              setFieldValue("name", search);
            }}
            onMenuClose={() => {}}
            onBlur={handleBlur}
            hasIcon={false}
            errors={errors?.email && touched.name ? errors?.email : ""}
            menuClassName="!max-h-[250px] !min-w-[200px] md:!z-[999]"
            position="bottom start"
            onSelectOption={(val: { name: string; value: string }) => {
              setFieldValue("name", val.name);
              setFieldValue("email", val.value);
            }}
            className=""
            hasTitle
            title="Search Member"
            containerClassName="!w-full"
            immediate
            loading={fetchingMembers}
          />

          <NumericFormat
            suffix={undefined}
            prefix={undefined}
            decimalScale={2}
            thousandSeparator
            allowLeadingZeros={false}
            customInput={TextInput}
            onBlur={handleBlur}
            onChange={handleChange}
            type="text"
            name={"amount"}
            value={values.amount}
            placeholder="Enter amount"
            errors={errors?.amount && touched?.amount ? errors?.amount : ""}
            inputClass=""
            className=""
            customIcon={
              <div>
                <Icon svg={"naira"} width={20} height={20} />
              </div>
            }
            customIconPosition="left"
            hasTitle
            title="Amount to Guarantee"
          />

          <Button
            type="submit"
            className="!w-fit !max-w-fit ml-auto"
            size="medium"
            text="Add Guarantor"
            variation="primary-dark"
            icon="plus"
            disabled={totalGuarantorAmountLeft === 0}
          />
        </>
      )}

      {finalPayload?.guarantor?.map((guarantor) => {
        return (
          <FormCard
            title={`${guarantor.email}`}
            text={`To Guarantee: ${formatToCurrency(guarantor.amount, {
              currency: "NGN",
              decimalPlaces: 2,
            })}`}
            icon={"profile"}
            barClassName="!bg-dark-purple !text-sm line-clamp-1 overflow-hidden text-ellipses"
            bodyClassName="!font-medium !text-sm !text-dark-purple"
            mode="edit"
            onRemove={() => {
              const currentGuarantors = finalPayload?.guarantor ?? [];

              setFinalPayload({
                ...finalPayload,
                guarantor: currentGuarantors?.filter(
                  (g) => g.email !== guarantor.email
                ),
              });
            }}
            className="!text-dark-purple"
            key={guarantor.email}
          />
        );
      })}

      <div className="pt-4 mt-auto border-t">
        <Button
          type="button"
          className=""
          text={"Proceed"}
          variation={"primary"}
          disabled={totalGuarantorAmountLeft > 0}
          onClick={() => {
            if (totalGuarantorAmountLeft === 0) {
              onSuccess();
            }
          }}
        />
      </div>
    </form>
  );
};
